import { valueOf } from "@/types/common";

export const USER_TYPE = {
  ADMIN: "admin",
  ADMIN_MEMBER: "admin_member",
  COMPANY_USER: "company_user",
};

export type TUserType = valueOf<typeof USER_TYPE>;

export const ADMIN_ROLE = {
  MANAGER: "manager",
  SALE: "company_sale",
  CUSTOM_SERVICE: "custom_service",
  AUDITOR: "auditor",
};

export type TAdminRole = valueOf<typeof ADMIN_ROLE>;

export const EMPLOYMENT_STATUS = {
  OFFERED: "offered",
  WORKING: "working",
  LEFT: "left",
};

export type TEmploymentStatus = valueOf<typeof EMPLOYMENT_STATUS>;

export const RETIREMENT_STATUS = {
  ENROLLMENT: "enrollment",
  PROCESSING: "processing",
  RETIRED: "retired",
};

export type TRetirementStatus = valueOf<typeof RETIREMENT_STATUS>;

export const COMPANY_DIVISION = {
  CORPORATE: "corporate",
  SOLE_PROPRIETOR: "sole_proprietor",
};

export type TCompanyDivision = valueOf<typeof COMPANY_DIVISION>;

export const GENDER = {
  MALE: "male",
  FEMALE: "female",
} as const;

export type TGender = valueOf<typeof GENDER>;

export const GENDER_LABEL = {
  [GENDER.MALE]: "男性",
  [GENDER.FEMALE]: "女性",
};

export const DOCUMENT_KEYS = {
  ACCEPTANCE_ACTIVITY: "acceptance_activity",
  LIFE_SUPPORT: "life_support",
  ANY_TIME: "any_time",
  RETIREMENT_ACCEPTING_WORKERS: "retirement_accepting_workers",
  RETIREMENT_EMPLOYMENT_CONTRACT: "retirement_employment_contract",
};

export type TDocumentKey = valueOf<typeof DOCUMENT_KEYS>;

export const DOCUMENT_STEP_KEYS = {
  ACCEPTANCE_ACTIVITY_CREATE_DOCUMENT: "acceptance_activity_create_document",
  ACCEPTANCE_ACTIVITY_UPLOAD_DOCUMENT: "acceptance_activity_upload_document",
  ACCEPTANCE_ACTIVITY_SUBMIT: "acceptance_activity_submit",
  LIFE_SUPPORT_CREATE_DOCUMENT: "life_support_create_document",
  LIFE_SUPPORT_CONFIRM_DISCUSSION_RECORD: "life_support_confirm_discussion_record",
  LIFE_SUPPORT_SUBMIT: "life_support_activity_submit",
  ANY_TIME_CREATE_DOCUMENT: "any_time_create_document",
  ANY_TIME_UPLOAD_DOCUMENT: "any_time_upload_document",
  ANY_TIME_SUBMIT: "any_time_submit",
  RENEW_VISA_BASIC_INFORMATION: "renew_visa_basic_information",
  RENEW_VISA_CREATE_DOCUMENT: "renew_visa_create_document",
  RENEW_VISA_SUBMIT: "renew_visa_submit",
  RENEW_VISA_AFTER_SUBMIT_APPLICATION: "renew_visa_after_submit_application",
  RENEW_VISA_UPDATE_RESIDENCE_CARD: "renew_visa_update_residence_card",
  RENEW_VISA_GROUP_BASIC_INFORMATION: "renew_visa_group_basic_information",
  RENEW_VISA_GROUP_CREATE_DOCUMENT: "renew_visa_group_create_document",
  RENEW_VISA_GROUP_SUBMIT: "renew_visa_group_submit",
  RENEW_VISA_GROUP_AFTER_SUBMIT_APPLICATION: "renew_visa_group_after_submit_application",
  RENEW_VISA_GROUP_AFTER_SUBMIT_APPLICATION_RESULT: "renew_visa_group_after_submit_application_result",
  RENEW_VISA_GROUP_UPDATE_RESIDENCE_CARD: "renew_visa_group_update_residence_card",
  RETIREMENT_ACCEPTING_WORKERS_CREATE_DOCUMENT: "retirement_accepting_workers_create_document",
  RETIREMENT_ACCEPTING_WORKERS_CREATE_CONVERT_REQUEST: "retirement_accepting_workers_create_convert_request",
  RETIREMENT_ACCEPTING_WORKERS_SUBMIT: "retirement_accepting_workers_submit",
  RETIREMENT_EMPLOYMENT_CONTRACT_CREATE_DOCUMENT: "retirement_employment_contract_create_document",
  RETIREMENT_EMPLOYMENT_CONTRACT_CONVERT_REQUEST: "retirement_employment_contract_convert_request",
  RETIREMENT_EMPLOYMENT_CONTRACT_SUBMIT: "retirement_employment_contract_submit",
};

export type TDocumentStepKey = valueOf<typeof DOCUMENT_STEP_KEYS>;

export const DOCUMENT_GROUPS = {
  [DOCUMENT_KEYS.ACCEPTANCE_ACTIVITY]: [
    DOCUMENT_STEP_KEYS.ACCEPTANCE_ACTIVITY_CREATE_DOCUMENT,
    DOCUMENT_STEP_KEYS.ACCEPTANCE_ACTIVITY_UPLOAD_DOCUMENT,
    DOCUMENT_STEP_KEYS.ACCEPTANCE_ACTIVITY_SUBMIT,
  ],
  [DOCUMENT_KEYS.LIFE_SUPPORT]: [
    DOCUMENT_STEP_KEYS.LIFE_SUPPORT_CREATE_DOCUMENT,
    DOCUMENT_STEP_KEYS.LIFE_SUPPORT_CONFIRM_DISCUSSION_RECORD,
    DOCUMENT_STEP_KEYS.LIFE_SUPPORT_SUBMIT,
  ],
  [DOCUMENT_KEYS.ANY_TIME]: [DOCUMENT_STEP_KEYS.ANY_TIME_CREATE_DOCUMENT, DOCUMENT_STEP_KEYS.ANY_TIME_SUBMIT],
  [DOCUMENT_KEYS.RETIREMENT_ACCEPTING_WORKERS]: [
    DOCUMENT_STEP_KEYS.RETIREMENT_ACCEPTING_WORKERS_CREATE_DOCUMENT,
    DOCUMENT_STEP_KEYS.RETIREMENT_ACCEPTING_WORKERS_CREATE_CONVERT_REQUEST,
    DOCUMENT_STEP_KEYS.RETIREMENT_ACCEPTING_WORKERS_SUBMIT,
  ],
  [DOCUMENT_KEYS.RETIREMENT_EMPLOYMENT_CONTRACT]: [
    DOCUMENT_STEP_KEYS.RETIREMENT_EMPLOYMENT_CONTRACT_CREATE_DOCUMENT,
    DOCUMENT_STEP_KEYS.RETIREMENT_EMPLOYMENT_CONTRACT_CONVERT_REQUEST,
    DOCUMENT_STEP_KEYS.RETIREMENT_EMPLOYMENT_CONTRACT_SUBMIT,
  ],
};

export const DOCUMENT_STATUS = {
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
  CONVERTING: "converting",
  CONVERT_COMPLETE: "convert_complete",
  ERROR: "error",
  SKIP: "skip",
  COMPLETED_CONVERT: "completed_convert",
};

export type TDocumentStatus = valueOf<typeof DOCUMENT_STATUS>;

export const DOCUMENT_STATUS_LABEL = {
  [DOCUMENT_STATUS.IN_PROGRESS]: "未完了",
  [DOCUMENT_STATUS.COMPLETE]: "完了",
  [DOCUMENT_STATUS.CONVERTING]: "変換中",
  [DOCUMENT_STATUS.CONVERT_COMPLETE]: "完了",
  [DOCUMENT_STATUS.COMPLETED_CONVERT]: "完了",
  [DOCUMENT_STATUS.ERROR]: "エラー",
  [DOCUMENT_STATUS.SKIP]: "スキップ",
};

export const CONVERT_STATUS = {
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
  ERROR: "error",
};

export type TConvertStatus = valueOf<typeof CONVERT_STATUS>;

export const EMPLOYEE_RETIREMENT_DOCUMENT_STEP_STATUS = {
  INCOMPLETE: "incomplete",
  PROCESSING: "processing",
  CREATING: "creating",
  COMPLETE: "complete",
  ERROR: "error",
};

export type TEmployeeRetirementDocumentStepStatus = valueOf<typeof EMPLOYEE_RETIREMENT_DOCUMENT_STEP_STATUS>;

export const EMPLOYEE_RETIREMENT_DOCUMENT_STEP_STATUS_LABEL = {
  [EMPLOYEE_RETIREMENT_DOCUMENT_STEP_STATUS.INCOMPLETE]: "未完了",
  [EMPLOYEE_RETIREMENT_DOCUMENT_STEP_STATUS.PROCESSING]: "処理中",
  [EMPLOYEE_RETIREMENT_DOCUMENT_STEP_STATUS.CREATING]: "作成中",
  [EMPLOYEE_RETIREMENT_DOCUMENT_STEP_STATUS.COMPLETE]: "完了",
  [EMPLOYEE_RETIREMENT_DOCUMENT_STEP_STATUS.ERROR]: "エラー",
};

export const RENEW_VISA_DOCUMENT_STEP_STATUS = {
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
};

export const RENEW_VISA_DOCUMENT_STATUS = {
  NOT_CREATED: "not_created",
  IN_PROGRESS: "in_progress",
  UNDER_REVIEW: "under_review",
  APPROVED: "approved",
  COMPLETE: "complete",
  WITHDRAWN: "withdrawn",
  REJECTED: "rejected",
};

export type TRenewVisaDocumentStatus = valueOf<typeof RENEW_VISA_DOCUMENT_STATUS>;

export const RENEW_VISA_DOCUMENT_STATUS_LABEL = {
  [RENEW_VISA_DOCUMENT_STATUS.NOT_CREATED]: "未作成",
  [RENEW_VISA_DOCUMENT_STATUS.IN_PROGRESS]: "準備中",
  [RENEW_VISA_DOCUMENT_STATUS.UNDER_REVIEW]: "審査中",
  [RENEW_VISA_DOCUMENT_STATUS.APPROVED]: "許可済",
  [RENEW_VISA_DOCUMENT_STATUS.COMPLETE]: "完了",
  [RENEW_VISA_DOCUMENT_STATUS.WITHDRAWN]: "取り下げ",
  [RENEW_VISA_DOCUMENT_STATUS.REJECTED]: "不許可",
};

export const RENEW_VISA_APPLICATION_RESULT = {
  APPROVED: "approved",
  WITHDRAWN: "withdrawn",
  REJECTED: "rejected",
};

export const RENEW_VISA_APPLICATION_RESULT_LABEL = {
  [RENEW_VISA_APPLICATION_RESULT.APPROVED]: "申請許可",
  [RENEW_VISA_APPLICATION_RESULT.WITHDRAWN]: "取り下げ",
  [RENEW_VISA_APPLICATION_RESULT.REJECTED]: "不許可",
};

export const RENEW_VISA_DOCUMENT_TYPE = {
  PREPARING: "preparing",
  SUBMITTED: "submitted",
  COMPLETED: "completed",
};

export type TRenewVisaDocumentType = valueOf<typeof RENEW_VISA_DOCUMENT_TYPE>;

export const SUPPORT_IMPLEMENTATION_STATUS = {
  SUPPORTED: "supported",
  NOT_SUPPORTED: "not_supported",
  NO_PARTICIPANT: "no_participant",
};

export type TSupportImplementationStatus = valueOf<typeof SUPPORT_IMPLEMENTATION_STATUS>;

export const SUPPORT_IMPLEMENTATION_STATUS_OPTIONS = [
  { label: "実施", value: SUPPORT_IMPLEMENTATION_STATUS.SUPPORTED },
  { label: "未実施", value: SUPPORT_IMPLEMENTATION_STATUS.NOT_SUPPORTED },
  { label: "支援対象者なし", value: SUPPORT_IMPLEMENTATION_STATUS.NO_PARTICIPANT },
];

export const EMPLOYEE_TRAINING_STATUS = {
  UN_SELECTED: "un_selected",
  YES: "yes",
  NO: "no",
};

export type TEmployeeTrainingStatus = valueOf<typeof EMPLOYEE_TRAINING_STATUS>;

export const EMPLOYEE_CAREER_LOCATION = {
  JAPAN: "japan",
  OUTSIDE_JAPAN: "outside_japan",
};

export type TEmployeeCareerLocation = valueOf<typeof EMPLOYEE_CAREER_LOCATION>;

export const EMPLOYEE_RESIDENCE_STATUS = {
  SPECIFIC_SKILL_ONE: "specific_skill_one",
  SPECIFIC_SKILL_TWO: "specific_skill_two",
  SPECIFIC_ACTIVITY: "specific_activity",
};

export type TEmployeeResidenceStatus = valueOf<typeof EMPLOYEE_RESIDENCE_STATUS>;

export const EMPLOYEE_RESIDENCE_PERIOD = {
  ONE_MONTH: "one_month",
  TWO_MONTH: "two_month",
  THREE_MONTH: "three_month",
  FOUR_MONTH: "four_month",
  FIVE_MONTH: "five_month",
  SIX_MONTH: "six_month",
  SEVEN_MONTH: "seven_month",
  EIGHT_MONTH: "eight_month",
  NINE_MONTH: "nine_month",
  TEN_MONTH: "ten_month",
  ELEVEN_MONTH: "eleven_month",
  TWELVE_MONTH: "twelve_month",
  ONE_YEAR: "one_year",
  THREE_YEAR: "three_year",
  FIVE_YEAR: "five_year",
};

export type TEmployeeResidencePeriod = valueOf<typeof EMPLOYEE_RESIDENCE_PERIOD>;

export const MAINTENANCE_MODE = {
  SYSTEM_NORMAL_OPERATION: "system_normal_operation",
  MAINTENANCE_READABLE: "maintenance_readable",
  MAINTENANCE_UNUSABLE: "maintenance_unusable",
};

export const SUPPORT_SPECIFIED_SKILL = {
  NAME_OR_TITLE: "name_or_title",
  ADDRESS: "address",
  CORPORATE_NUMBER: "corporate_number",
  OTHER: "other",
  SUPPORT_MANAGER_NAME_AND_POSITION: "support_manager_name_and_position",
  NUMBER_OF_FOREIGN_WORKERS: "number_of_foreign_workers",
  NUMBER_OF_SUPPORT_STAFF: "number_of_support_staff",
  OFFICE_LOCATION: "office_location",
};

export const YES_NO_ANSWER = {
  YES: "yes",
  NO: "no",
};

export type TYesNoAnswer = valueOf<typeof YES_NO_ANSWER>;

export const YES_NO_AND_NOT_APPLICABLE_ANSWER = {
  YES: "yes",
  NO: "no",
  NOT_APPLICABLE: "not_applicable",
};

export type TYesNoAndNotApplicableAnswer = valueOf<typeof YES_NO_AND_NOT_APPLICABLE_ANSWER>;

export const COMPANY_SUPPORTER_ROLE = {
  CONTACT: "contact",
  SUPPORT_MANAGER: "support_manager",
  SUPPORT: "support",
};

export const RESIDENCE_STATUS = {
  SPECIFIED_SKILL_NO1: 1,
  SPECIFIED_SKILL_NO2: 2,
  SPECIFIED_ACTIVITY: 3,
} as const;

export type TResidenceStatus = valueOf<typeof RESIDENCE_STATUS>;

export const CHECK_UPDATE_EMPLOYEE_RESULT = {
  CAN_UPDATE: "can_update",
  CAN_UPDATE_AND_DELETE_RENEW_VISA: "can_update_and_delete_renew_visa",
  DELETE_RENEW_VISA_AND_SHOW_CONFIRM_MODAL: "delete_renew_visa_and_show_confirm_modal",
  CAN_UPDATE_AND_UPDATE_RENEW_VISA: "can_update_and_update_renew_visa",
  CAN_UPDATE_AND_CREATE_RENEW_VISA: "can_update_and_create_renew_visa",
  CAN_NOT_UPDATE_RENEW_VISA: "can_not_update_renew_visa",
};

export type TCheckUpdateEmployeeResult = valueOf<typeof CHECK_UPDATE_EMPLOYEE_RESULT>;

export const CHECK_COMPLETE_RETIREMENT_RESULT = {
  CAN_UPDATE: "can_update",
  CAN_UPDATE_AND_DELETE_RENEW_VISA: "can_update_and_delete_renew_visa",
};

export type TCheckToCompleteRetirementResult = valueOf<typeof CHECK_COMPLETE_RETIREMENT_RESULT>;

export const JAPAN_NATIONALITY_NAME = "日本（国内）";

export const RESIDENCE_STATUS_FOR_COE_NAME = "未取得（認定申請前）";

export const COMPANY_SPECIFIC_INDUSTRY_NAME = {
  FOOD_SERVICE: "外食業", //1
  FOOD_AND_BEVERAGE_MANUFACTURING: "飲食料品製造業", //2
  FISHERY: "漁業", //3
  AGRICULTURE: "農業", //4
  ACCOMMODATION: "宿泊", // 5
  AVIATION: "航空", // 6
  AUTOMOBILE_MAINTENANCE: "自動車整備", // 7
  SHIPBUILDING: "造船・舶用工業", // 8
  CONSTRUCTION: "建設", // 9
  INDUSTRIAL_PRODUCT_MANUFACTURING: "工業製品製造業", // 10
  BUILDING_CLEANING: "ビルクリーニング", // 11
  NURSING_CARE: "介護", // 12
  WOOD_INDUSTRY: "木材産業", // 木材産業
  FORESTRY: "林業", // 林業
  AUTOMOBILE_TRANSPORTATION: "自動車運送業", // 自動車運送業
  RAILWAY: "鉄道", // 鉄道
};

export type TCompanySpecificIndustryName = valueOf<typeof COMPANY_SPECIFIC_INDUSTRY_NAME>;

export const COMPANY_CONDITION = {
  A: "a",
  B: "b",
  C: "c",
  D: "d",
  E: "e",
  NONE_A_TO_E: "none_a_to_e",
};

export type TCompanyCondition = valueOf<typeof COMPANY_CONDITION>;

export const CHECK_PDF_RESULT = {
  OK: "ok",
  INVALID: "invalid",
  PASSWORD_PROTECTED: "password-protected",
};

export const ENTRUST_ANSWER = {
  NOT_ENTRUST: "not_entrust",
  ENTRUST: "entrust",
};
